interface BarChartProps {
  data: Array<{ label: string; value: number }>;
  title?: string;
}

export function BarChart({ data }: BarChartProps) {
  if (data.length === 0) {
    return (
      <div className='flex h-64 items-center justify-center text-gray-400 dark:text-gray-500'>
        No data available
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const chartHeight = 200;
  const chartWidth = 400;
  const barWidth = Math.min(24, (chartWidth - 40) / data.length - 4);
  const padding = { top: 20, right: 20, bottom: 40, left: 40 };

  // Y-axis scale
  const yScale = (value: number) =>
    chartHeight -
    padding.bottom -
    (value / maxValue) * (chartHeight - padding.top - padding.bottom);

  // Generate Y-axis ticks
  const yTicks = [0, Math.ceil(maxValue / 2), maxValue];

  return (
    <div className='flex flex-col items-center'>
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className='h-64 w-full'
        preserveAspectRatio='xMidYMid meet'
      >
        {/* Y-axis */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={chartHeight - padding.bottom}
          stroke='currentColor'
          className='text-gray-300 dark:text-gray-600'
          strokeWidth='1'
        />

        {/* Y-axis ticks and labels */}
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={padding.left - 5}
              y1={yScale(tick)}
              x2={padding.left}
              y2={yScale(tick)}
              stroke='currentColor'
              className='text-gray-300 dark:text-gray-600'
              strokeWidth='1'
            />
            <text
              x={padding.left - 8}
              y={yScale(tick) + 4}
              textAnchor='end'
              className='fill-gray-500 text-[10px] dark:fill-gray-400'
            >
              {tick}
            </text>
            {/* Grid line */}
            <line
              x1={padding.left}
              y1={yScale(tick)}
              x2={chartWidth - padding.right}
              y2={yScale(tick)}
              stroke='currentColor'
              className='text-gray-200 dark:text-gray-700'
              strokeWidth='1'
              strokeDasharray='4,4'
            />
          </g>
        ))}

        {/* X-axis */}
        <line
          x1={padding.left}
          y1={chartHeight - padding.bottom}
          x2={chartWidth - padding.right}
          y2={chartHeight - padding.bottom}
          stroke='currentColor'
          className='text-gray-300 dark:text-gray-600'
          strokeWidth='1'
        />

        {/* Bars */}
        {data.map((item, index) => {
          const barHeight = (item.value / maxValue) * (chartHeight - padding.top - padding.bottom);
          const x =
            padding.left +
            10 +
            index * ((chartWidth - padding.left - padding.right - 20) / data.length);
          const y = chartHeight - padding.bottom - barHeight;

          // Format date label (MM/DD)
          const dateLabel = item.label.slice(5).replace('-', '/');

          return (
            <g key={index}>
              {/* Bar */}
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(barHeight, 0)}
                className='fill-blue-500 transition-all hover:fill-blue-600 dark:fill-blue-400 dark:hover:fill-blue-500'
                rx='2'
              />
              {/* Value label on top of bar */}
              {item.value > 0 && (
                <text
                  x={x + barWidth / 2}
                  y={y - 4}
                  textAnchor='middle'
                  className='fill-gray-600 text-[9px] font-medium dark:fill-gray-300'
                >
                  {item.value}
                </text>
              )}
              {/* X-axis label (show every other label if too many) */}
              {(data.length <= 7 || index % 2 === 0) && (
                <text
                  x={x + barWidth / 2}
                  y={chartHeight - padding.bottom + 14}
                  textAnchor='middle'
                  className='fill-gray-500 text-[9px] dark:fill-gray-400'
                >
                  {dateLabel}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Summary */}
      <div className='mt-2 text-center text-sm text-gray-600 dark:text-gray-400'>
        Total: {data.reduce((sum, d) => sum + d.value, 0).toLocaleString()} registrations in last 14
        days
      </div>
    </div>
  );
}
